import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const GRID = 13;
const N = GRID - 1;
const STEP = 24 / N;
const C = N / 2;
const R = N / 2 - 0.6;

const ring = (x, y, cx, cy, r, t = 0.5) => {
  const d = Math.hypot(x - cx, y - cy);
  return d >= r - t && d <= r + t;
};

const peaceMask = Array.from({ length: GRID }, (_, y) =>
  Array.from({ length: GRID }, (_, x) => {
    if (ring(x, y, C, C, R)) return true;
    if (Math.abs(x - C) <= 0.4 && y >= C && y <= N) return true;
    if (Math.abs(x - y) <= 0.5 && x <= C && y <= C) return true;
    if (Math.abs(x + y - N) <= 0.5 && x >= C && y <= C) return true;
    return false;
  })
);

const R2 = R / 2;
const cTop = C - R2;
const cBot = C + R2;

const yinYangMask = Array.from({ length: GRID }, (_, y) =>
  Array.from({ length: GRID }, (_, x) => {
    if (ring(x, y, C, C, R)) return true;
    if (ring(x, y, C, cTop, R2) && x >= C) return true;
    if (ring(x, y, C, cBot, R2) && x <= C) return true;
    if (Math.hypot(x - (C - 1.7), y - cTop) <= 0.85) return true;
    if (Math.hypot(x - (C + 1.7), y - cBot) <= 0.85) return true;
    return false;
  })
);

const thumbMask = Array.from({ length: GRID }, (_, y) =>
  Array.from({ length: GRID }, (_, x) => {
    const fist = ((x - (C + 0.2)) / 3.3) ** 2 + ((y - (C + 3.4)) / 2.4) ** 2 <= 1;
    const bar = x >= C - 3.4 && x <= C - 1.4 && y >= 2.4 && y <= C + 2.8;
    const knuckle = Math.hypot(x - (C - 2.4), y - 2.4) <= 1.1;
    return fist || bar || knuckle;
  })
);

const smileyMask = Array.from({ length: GRID }, (_, y) =>
  Array.from({ length: GRID }, (_, x) => {
    if (ring(x, y, C, C, R)) return true;
    if (Math.hypot(x - (C - 2.3), y - (C - 1.8)) <= 0.8) return true;
    if (Math.hypot(x - (C + 2.3), y - (C - 1.8)) <= 0.8) return true;
    if (ring(x, y, C, C + 1.9, 3.0) && y >= C + 0.8) return true;
    return false;
  })
);

const DotGrid = ({ mask }) => {
  const dots = [];
  for (let y = 0; y < GRID; y++)
    for (let x = 0; x < GRID; x++) if (mask[y][x]) dots.push([x * STEP, y * STEP]);

  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-full w-full">
      {dots.map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r={0.8} fill="currentColor" />
      ))}
    </svg>
  );
};

const Peace = () => <DotGrid mask={peaceMask} />;
const YinYang = () => <DotGrid mask={yinYangMask} />;
const ThumbsUp = () => <DotGrid mask={thumbMask} />;
const Smiley = () => <DotGrid mask={smileyMask} />;

const GLYPHS = [
  { id: "peace", icon: <Peace /> },
  { id: "yin-yang", icon: <YinYang /> },
  { id: "thumbs-up", icon: <ThumbsUp /> },
  { id: "smiley", icon: <Smiley /> },
];

const CYCLE_MS = 3400;

export const NothingGlyph = ({ onOpen }) => {
  const reducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reducedMotion) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % GLYPHS.length), CYCLE_MS);
    return () => clearInterval(t);
  }, [reducedMotion]);

  const glyph = GLYPHS[reducedMotion ? 0 : index];

  return (
    <div className="fixed top-3 right-3 z-40 select-none">
      <div className="flex flex-col items-end gap-1.5 group">
        <motion.button
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.94 }}
          onClick={onOpen}
          aria-label="Open passport"
          title="OPEN PASSPORT"
          className="relative h-10 w-10 text-ink rounded-full border border-gray-200 bg-white/80 backdrop-blur-sm shadow-sm flex items-center justify-center cursor-pointer hover:border-nothing-red hover:text-nothing-red transition-colors"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={glyph.id}
              initial={{ opacity: 0, scale: 0.88, rotate: -6 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.9, rotate: 6 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="absolute inset-0 flex items-center justify-center"
            >
              {glyph.icon}
            </motion.div>
          </AnimatePresence>
        </motion.button>
        <div className="flex items-center gap-1.5 justify-end">
          <span className={`h-1.5 w-1.5 rounded-full bg-nothing-red ${reducedMotion ? "" : "animate-pulse"}`} />
          <span className="text-[9px] font-mono tracking-widest text-gray-400 group-hover:text-nothing-red transition-colors">
            PASSPORT
          </span>
        </div>
      </div>
    </div>
  );
};